import {
  Action,
  Description,
  Provider,
  Root,
  Title,
  Viewport
} from "@radix-ui/react-toast";
import { createPortal } from "react-dom";
import { IconButton } from "../iconButton/IconButton";
import {
  toastClose,
  toastContent,
  toastDescription,
  toastTitleVariants,
  toastVariants,
  toastViewport
} from "./Toast.css";

export interface ToastProps {
  /**
   * Whether the toast is visible or not
   */
  open: boolean;
  /**
   * Callback function to set the open status of the
   * toast message
   */
  setOpen: (open: boolean) => void;
  /**
   * Determines the styling of the toast, ie. background colour.
   */
  variant?: keyof typeof toastVariants;
  /**
   * Title of the message to be displayed
   */
  title?: string;
  /**
   * Additional text to be displayed under the title
   */
  description?: string;
  /**
   * Time in milliseconds that toast should remain visible for.
   */
  duration?: number;
}

/**
 * A succinct message that is displayed temporarily.
 *
 * To be used as a notification or to notify the user that an error occurred.
 */
export function Toast({
  open,
  setOpen,
  variant = "default",
  title,
  description,
  duration = 5000
}: ToastProps) {
  return (
    <Provider>
      <Root
        className={toastVariants[variant]}
        open={open}
        onOpenChange={setOpen}
        duration={duration}
      >
        <div className={toastContent}>
          {title && (
            <Title className={toastTitleVariants[variant]}>{title}</Title>
          )}
          {description && (
            <Description className={toastDescription}>
              {description}
            </Description>
          )}
        </div>
        <Action asChild altText="Close notification">
          <IconButton
            className={toastClose}
            iconName="close"
            variant="tertiary"
          />
        </Action>
      </Root>
      {createPortal(<Viewport className={toastViewport} />, document.body)}
    </Provider>
  );
}
